import { QuestionCircleOutlined } from '@ant-design/icons'
import { Modal } from 'antd'
import * as FileSaver from 'file-saver'
import htmlToPdfmake from 'html-to-pdfmake'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import React from 'react'
import * as XLSX from 'xlsx'
import { RISK_TYPE } from './constants'

/** App utils */
const { confirm } = Modal

const INCH_TO_POINT = 72
const MM_TO_POINT = 2.834645669291339
const PX_TO_POINT = 0.75
const A4_PAGE_WIDTH_POINT = 595.28
const DEFAULT_PDF_PAGE_MARGIN_POINT = 40
const DEFAULT_PDF_CONTENT_WIDTH_POINT =
  A4_PAGE_WIDTH_POINT - DEFAULT_PDF_PAGE_MARGIN_POINT * 2

const toPointWidth = rawWidth => {
  if (typeof rawWidth !== 'string') return null

  const normalizedWidth = rawWidth.trim().toLowerCase()
  const numericWidth = parseFloat(normalizedWidth)

  if (!Number.isFinite(numericWidth) || numericWidth <= 0) return null

  if (normalizedWidth.endsWith('pt')) return numericWidth
  if (normalizedWidth.endsWith('in')) return numericWidth * INCH_TO_POINT
  if (normalizedWidth.endsWith('mm')) return numericWidth * MM_TO_POINT
  if (normalizedWidth.endsWith('cm')) return numericWidth * MM_TO_POINT * 10
  if (normalizedWidth.endsWith('px')) return numericWidth * PX_TO_POINT
  if (normalizedWidth.endsWith('%')) {
    const safePercent = Math.max(0, Math.min(numericWidth, 100))
    return (safePercent / 100) * DEFAULT_PDF_CONTENT_WIDTH_POINT
  }

  return numericWidth * PX_TO_POINT
}

const parseWidthToPoint = widthValue => {
  if (typeof widthValue === 'number') {
    return Number.isFinite(widthValue) && widthValue > 0 ? widthValue : null
  }

  if (typeof widthValue !== 'string') return null

  return toPointWidth(widthValue)
}

const normalizeFigureImageWidthForPdf = htmlContent => {
  if (
    typeof DOMParser === 'undefined' ||
    typeof htmlContent !== 'string' ||
    !htmlContent.includes('<figure')
  ) {
    return htmlContent
  }

  try {
    const parser = new DOMParser()
    const documentNode = parser.parseFromString(
      `<div id="pdf-export-root">${htmlContent}</div>`,
      'text/html'
    )
    const root = documentNode.getElementById('pdf-export-root')

    if (!root) return htmlContent

    root.querySelectorAll('figure').forEach(figure => {
      const styleWidth = figure.style?.width
      if (!styleWidth) return

      const image = figure.querySelector('img')
      if (!image) return

      const widthInPoint = toPointWidth(styleWidth)
      if (!widthInPoint) return

      const existingStyle = image.getAttribute('style') || ''
      const cleanedStyle = existingStyle
        .replace(/(^|;)\s*width\s*:[^;]*/gi, '$1')
        .replace(/(^|;)\s*height\s*:[^;]*/gi, '$1')
        .replace(/(^|;)\s*max-width\s*:[^;]*/gi, '$1')
        .trim()

      const styleSuffix = cleanedStyle && !cleanedStyle.endsWith(';') ? ';' : ''
      image.setAttribute(
        'style',
        `${cleanedStyle}${styleSuffix} width: ${widthInPoint}pt; height: auto;`.trim()
      )
      image.setAttribute('width', `${Math.round(widthInPoint * 100) / 100}`)
      figure.style.removeProperty('width')
    })

    return root.innerHTML
  } catch {
    return htmlContent
  }
}

const sanitizeHtmlForPdfMake = htmlContent => {
  if (typeof htmlContent !== 'string') return ''

  let sanitizedHtml = htmlContent

  // Normalize non-breaking spaces copied from office editors so text can wrap.
  sanitizedHtml = sanitizedHtml
    .replace(/&(nbsp|#160|#xa0);/gi, ' ')
    .replace(/[\u00A0\u202F]/g, ' ')

  // CKEditor puts resized width on <figure>, but html-to-pdfmake reads width from <img>.
  sanitizedHtml = normalizeFigureImageWidthForPdf(sanitizedHtml)

  // Avoid rgba alpha parsing edge-cases in html-to-pdfmake.
  sanitizedHtml = sanitizedHtml.replace(
    /rgba\(\d+,\d+,\d+,\d(?:\.\d+)?\);/g,
    'rgb(0,0,0);'
  )

  // Convert inch and mm margins to points to avoid unsupported conversions.
  sanitizedHtml = sanitizedHtml.replace(
    /(margin(?:-left|-right|-top|-bottom)?):\s*(-?\d+(?:\.\d+)?)in\s*(;|")/gi,
    (match, property, value, terminator) => {
      const ptValue = parseFloat(value) * INCH_TO_POINT
      return `${property}: ${ptValue}pt${terminator}`
    }
  )
  sanitizedHtml = sanitizedHtml.replace(
    /(margin(?:-left|-right|-top|-bottom)?):\s*(-?\d+(?:\.\d+)?)mm\s*(;|")/gi,
    (match, property, value, terminator) => {
      const ptValue = parseFloat(value) * MM_TO_POINT
      return `${property}: ${ptValue}pt${terminator}`
    }
  )

  // Hard replace NaN numeric tokens coming from pasted rich text styles.
  sanitizedHtml = sanitizedHtml.replace(
    /([:\s(])NaN(?:\.\d+)?([a-z%]*)/gi,
    (match, prefix, unit = '') => `${prefix}0${unit}`
  )

  // Replace calc(...) in numeric style fields because html-to-pdfmake cannot resolve it.
  sanitizedHtml = sanitizedHtml.replace(
    /(width|height|margin(?:-[a-z]+)?|padding(?:-[a-z]+)?|left|right|top|bottom|line-height|font-size)\s*:\s*calc\([^;"]+\)\s*(;|")/gi,
    (match, property, terminator) => `${property}: 0${terminator}`
  )

  // Force wrapping for rich-text fragments copied from office tools.
  sanitizedHtml = sanitizedHtml.replace(
    /white-space\s*:\s*nowrap\s*(;|")/gi,
    'white-space: normal$1'
  )
  sanitizedHtml = sanitizedHtml.replace(
    /word-break\s*:\s*keep-all\s*(;|")/gi,
    'word-break: break-word$1'
  )

  return sanitizedHtml
}

const sanitizePdfMakeValue = value => {
  if (Array.isArray(value)) {
    return value.map(item => sanitizePdfMakeValue(item))
  }

  if (value && typeof value === 'object') {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = sanitizePdfMakeValue(value[key])
      return acc
    }, {})
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }

  if (typeof value === 'string' && value.includes('NaN')) {
    return value.replace(/NaN(?:\.\d+)?/g, '0')
  }

  if (typeof value === 'string') {
    return value.replace(/[\u00A0\u202F]/g, ' ')
  }

  return value
}

const normalizeTableWidthsForPdf = widths => {
  if (!Array.isArray(widths)) return widths

  const hasFlexibleWidth = widths.some(width => {
    if (typeof width === 'string') {
      const normalizedWidth = width.trim().toLowerCase()
      return normalizedWidth === '*' || normalizedWidth === 'auto'
    }

    return false
  })

  const targetWidth = hasFlexibleWidth
    ? DEFAULT_PDF_CONTENT_WIDTH_POINT * 0.9
    : DEFAULT_PDF_CONTENT_WIDTH_POINT
  const parsedWidths = widths.map(width => parseWidthToPoint(width))
  const totalFixedWidth = parsedWidths.reduce(
    (sum, width) => (width ? sum + width : sum),
    0
  )

  if (totalFixedWidth <= 0 || totalFixedWidth <= targetWidth) return widths

  const scale = targetWidth / totalFixedWidth

  return widths.map((width, index) => {
    const parsedWidth = parsedWidths[index]
    if (!parsedWidth) return width

    return Math.max(10, Math.round(parsedWidth * scale * 100) / 100)
  })
}

const normalizePdfMakeLayout = value => {
  if (Array.isArray(value)) {
    return value.map(item => normalizePdfMakeLayout(item))
  }

  if (!value || typeof value !== 'object') return value

  const normalizedValue = Object.keys(value).reduce((acc, key) => {
    acc[key] = normalizePdfMakeLayout(value[key])
    return acc
  }, {})

  if (normalizedValue.noWrap === true) {
    normalizedValue.noWrap = false
  }

  if (Array.isArray(normalizedValue.margin)) {
    const safeMargin = [...normalizedValue.margin]

    if (typeof safeMargin[0] === 'number') {
      safeMargin[0] = Math.max(0, safeMargin[0])
    }
    if (typeof safeMargin[2] === 'number') {
      safeMargin[2] = Math.max(0, safeMargin[2])
    }

    normalizedValue.margin = safeMargin
  }

  ;['width', 'minWidth', 'maxWidth'].forEach(property => {
    const parsedWidth = parseWidthToPoint(normalizedValue[property])
    if (!parsedWidth) return

    normalizedValue[property] = Math.min(
      parsedWidth,
      DEFAULT_PDF_CONTENT_WIDTH_POINT
    )
  })

  if (Array.isArray(normalizedValue.fit) && normalizedValue.fit.length >= 1) {
    const parsedFitWidth = parseWidthToPoint(normalizedValue.fit[0])
    if (parsedFitWidth) {
      normalizedValue.fit[0] = Math.min(
        parsedFitWidth,
        DEFAULT_PDF_CONTENT_WIDTH_POINT
      )
    }
  }

  if (normalizedValue.table && Array.isArray(normalizedValue.table.widths)) {
    normalizedValue.table = {
      ...normalizedValue.table,
      widths: normalizeTableWidthsForPdf(normalizedValue.table.widths),
    }
  }

  return normalizedValue
}

const buildSafePdfContent = htmlContent => {
  const html = htmlToPdfmake(sanitizeHtmlForPdfMake(htmlContent), {
    imagesByReference: true,
  })

  const safeContent = sanitizePdfMakeValue(html.content)
  const safeImages = sanitizePdfMakeValue(html.images)

  return {
    content: normalizePdfMakeLayout(safeContent),
    images: normalizePdfMakeLayout(safeImages),
  }
}

const utils = {
  getBase64FromImageUrl(url) {
    return new Promise((resolve, reject) => {
      var img = new Image()
      img.setAttribute('crossOrigin', 'anonymous')

      img.onload = () => {
        var canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height

        var ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0)

        var dataURL = canvas.toDataURL('image/png')

        resolve(dataURL)
      }

      img.onerror = error => {
        reject(error)
      }

      img.src = url
    })
  },
  htmlToText(html) {
    const tempElement = document.createElement('div')
    tempElement.innerHTML = html
    return tempElement.textContent || tempElement.innerText || ''
  },
  htmlToTextWithDownLine(html) {
    // Thay thế các thẻ <br> bằng ký tự xuống dòng
    const withNewLines = html?.replace(/<br\s*[\/]?>/gi, '\n')
    const tempElement = document.createElement('div')
    tempElement.innerHTML = withNewLines
    return tempElement.textContent || tempElement.innerText || ''
  },
  renderTextWithLineBreaks(text) {
    return text?.split('\n').map((line, index, array) => (
      <React.Fragment key={index}>
        {line}
        {index < array.length - 1 && <br />}
      </React.Fragment>
    ))
  },

  /** Add commas for separate thousand in number */
  thousandSeparator: num => {
    let parts = num.toString().split('.')
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    return parts.join('.')
  },

  /** remove html */
  removeHtml: string => {
    if (!string) return ''
    return string
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, '')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/, '<')
      .replace(/&gt;/, '>')
      .replace(/&le;/, '≤')
      .replace(/&ge;/, '≥')
  },

  /** Compare time, return -1 for sooner, 0 for equal, 1 for later */
  compareTime: (DateA, DateB) => {
    let a = new Date(DateA)
    let b = new Date(DateB)
    let msDateA = Date.UTC(a.getFullYear(), a.getMonth() + 1, a.getDate())
    let msDateB = Date.UTC(b.getFullYear(), b.getMonth() + 1, b.getDate())
    if (parseFloat(msDateA) < parseFloat(msDateB)) return -1
    // lt
    else if (parseFloat(msDateA) === parseFloat(msDateB)) return 0
    // eq
    else if (parseFloat(msDateA) > parseFloat(msDateB)) return 1
    // gt
    else return null // error
  },

  removeVietnameseCharMark: str => {
    if (str) {
      str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a')
      str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e')
      str = str.replace(/ì|í|ị|ỉ|ĩ/g, 'i')
      str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o')
      str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u')
      str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y')
      str = str.replace(/đ/g, 'd')
      str = str.replace(/À|Á|Ạ|Ả|Ã|Â|Ầ|Ấ|Ậ|Ẩ|Ẫ|Ă|Ằ|Ắ|Ặ|Ẳ|Ẵ/g, 'A')
      str = str.replace(/È|É|Ẹ|Ẻ|Ẽ|Ê|Ề|Ế|Ệ|Ể|Ễ/g, 'E')
      str = str.replace(/Ì|Í|Ị|Ỉ|Ĩ/g, 'I')
      str = str.replace(/Ò|Ó|Ọ|Ỏ|Õ|Ô|Ồ|Ố|Ộ|Ổ|Ỗ|Ơ|Ờ|Ớ|Ợ|Ở|Ỡ/g, 'O')
      str = str.replace(/Ù|Ú|Ụ|Ủ|Ũ|Ư|Ừ|Ứ|Ự|Ử|Ữ/g, 'U')
      str = str.replace(/Ỳ|Ý|Ỵ|Ỷ|Ỹ/g, 'Y')
      str = str.replace(/Đ/g, 'D')
      return str.trim()
    }
    return str.trim()
  },

  nonAccentVietnamese: str => {
    if (typeof str !== 'string') return
    str = str.toLowerCase().trim()
    str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a')
    str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e')
    str = str.replace(/ì|í|ị|ỉ|ĩ/g, 'i')
    str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o')
    str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u')
    str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y')
    str = str.replace(/đ/g, 'd')
    // Một số hệ thống mã hóa tiếng Việt kết hợp dấu thành các ký tự utf-8 riêng lẻ
    str = str.replace(/\u0300|\u0301|\u0303|\u0309|\u0323/g, '') // Huyền sắc hỏi ngã nặng
    str = str.replace(/\u02C6|\u0306|\u031B/g, '') // Â, Ê, Ă, Ơ, Ư
    return str
  },

  /** Decode JWT token */
  jwtDecode: token => {
    let base64Url = token.split('.')[1]
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    let jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        })
        .join('')
    )
    return JSON.parse(jsonPayload)
  },

  /** Filter unique object in array */
  getUnique: (arr, comp) => {
    return arr
      .map(e => e[comp])
      .map((e, i, final) => final.indexOf(e) === i && i)
      .filter(e => arr[e])
      .map(e => arr[e])
  },

  /** Get gender name */
  getGenderName: gender => {
    let genderName = ''
    switch (gender) {
      case 0:
        genderName = 'Nữ'
        break
      case 1:
        genderName = 'Nam'
        break
      case 2:
        genderName = 'Khác'
        break
      default:
        genderName = undefined
        break
    }
    return genderName
  },

  /** Render weekday name */
  renderWeekday: day => {
    switch (day) {
      case 1:
        return 'Thứ hai'
      case 2:
        return 'Thứ ba'
      case 3:
        return 'Thứ tư'
      case 4:
        return 'Thứ năm'
      case 5:
        return 'Thứ sáu'
      case 6:
        return 'Thứ bảy'
      default:
        return 'Chủ nhật'
    }
  },

  /** Check nullish */
  isNullish: value => {
    return value === undefined || value === null || value === ''
  },

  /** Capitalize name */
  getNameInCapitalize: string => {
    if (!string) return ''
    return string.toLowerCase().replace(/(^|\s)\S/g, l => l.toUpperCase())
  },

  /** get extension file*/
  getExtensionFile: file_name => {
    if (!file_name || typeof file_name !== 'string') return ''
    return file_name.toLowerCase().split('.').pop()
  },
  /** Remove extension file from name file*/
  removeExtensionFile: file_name => {
    if (!file_name) return ''
    return file_name.split('.').slice(0, -1).join('.')
  },

  /** Get date range between 2 dates
   * Input values should be in string format (YYYY-MM-DD)
   * */
  getDateRange: date => {
    if (typeof date !== 'string') {
      throw new Error('Input date should be a string in format YYYY-MM-DD')
    }
    let d1 = new Date(date)
    let d2 = new Date()
    let diff = d1.getTime() - d2.getTime()
    return diff / (1000 * 3600 * 24)
  },

  isUpper: str => {
    if (typeof str !== 'string') return false
    return !/[a-z]/.test(str) && /[A-Z]/.test(str)
  },

  validURL: str => {
    var pattern = new RegExp(
      '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-z\\d_]*)?$',
      'i'
    ) // fragment locator
    return !!pattern.test(str)
  },

  /** Return the date range from week number and year
   * For example: Input weekNo = 3, year = 2020 will return 2020-01-13 and 2020-01-19
   * Remember to use moment('YYYY-MM-DD').isoWeekYear()
   * */
  getDateRangeOfWeek: (weekNo, yearNo) => {
    if (
      !(
        Number.isInteger(weekNo) &&
        Number.isInteger(yearNo) &&
        weekNo >= 1 &&
        yearNo >= 1970
      )
    )
      return false
    Date.prototype.getWeek = function () {
      let date = new Date(this.getTime())
      date.setHours(0, 0, 0, 0)
      date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7))
      let week1 = new Date(date.getFullYear(), 0, 4)
      return (
        1 +
        Math.round(
          ((date.getTime() - week1.getTime()) / 86400000 -
            3 +
            ((week1.getDay() + 6) % 7)) /
            7
        )
      )
    }
    let d1, numOfdaysPastSinceLastMonday, month, date
    let rangeIsFrom, rangeIsTo
    d1 = new Date('' + yearNo + '')
    numOfdaysPastSinceLastMonday = d1.getDay() - 1
    d1.setDate(d1.getDate() - numOfdaysPastSinceLastMonday)
    d1.setDate(d1.getDate() + 7 * (weekNo - d1.getWeek()))
    month = d1.getMonth() + 1 < 10 ? `0${d1.getMonth() + 1}` : d1.getMonth() + 1
    date = d1.getDate() < 10 ? `0${d1.getDate()}` : d1.getDate()
    rangeIsFrom = `${d1.getFullYear()}-${month}-${date}`
    d1.setDate(d1.getDate() + 6)
    month = d1.getMonth() + 1 < 10 ? `0${d1.getMonth() + 1}` : d1.getMonth() + 1
    date = d1.getDate() < 10 ? `0${d1.getDate()}` : d1.getDate()
    rangeIsTo = `${d1.getFullYear()}-${month}-${date}`
    return [rangeIsFrom, rangeIsTo]
  },
  renderDocumentCommitteeStatus: statusCode => {
    switch (statusCode) {
      case '00':
        return 'Tạo văn bản mới'
      case '01':
        return 'Đã đến'
      case '02':
        return 'Từ chối tiếp nhận'
      case '03':
        return 'Đã tiếp nhận văn bản'
      case '04':
        return 'Phân công xử lý'
      case '05':
        return 'Đang xử lý'
      case '06':
        return 'Hoàn thành'
      case '13':
        return 'Yêu cầu lấy lại văn bản'
      case '15':
        return 'Đồng ý lấy lại, cập nhật văn bản'
      case '16':
        return 'Từ chối lấy lại, cập nhật văn bản'
      case null:
        return 'Đã gửi'
      case '100':
        return 'Văn bản bị thu hồi'
      case '200':
        return 'Văn bản bị cập nhật'
      case '300':
        return 'Văn bản bị thay thế'
      default:
        return ''
    }
  },
  renderDocumentCommitteeTag: status => {
    const tag = {
      color: '',
      text: '',
    }
    switch (status) {
      case '01':
        tag.color = 'black'
        tag.name = 'Đã đến'
        break
      case '02':
        tag.color = 'red'
        tag.name = 'Từ chối tiếp nhận'
        break
      case '03':
        tag.color = 'purple'
        tag.name = 'Đã tiếp nhận'
        break
      case '04':
        tag.color = 'lime'
        tag.name = 'Phân công'
        break
      case '05':
        tag.color = 'green'
        tag.name = 'Đang xử lý'
        break
      case '06':
        tag.color = 'blue'
        tag.name = 'Hoàn thành'
        break
      case '13':
        tag.color = 'magenta'
        tag.name = 'Lấy lại'
        break
      case '15':
        tag.color = 'volcano'
        tag.name = 'Đồng ý cập nhật / lấy lại'
        break
      case '16':
        tag.color = 'red'
        tag.name = 'Từ chối cập nhật / lấy lại'
        break
      case null:
        tag.color = 'gray'
        tag.name = 'Chưa đọc'
        break
      case '100':
        tag.color = 'red'
        tag.name = 'Văn bản bị thu hồi'
        break
      case '200':
        tag.color = 'red'
        tag.name = 'Văn bản bị cập nhật'
        break
      case '300':
        tag.color = 'red'
        tag.name = 'Văn bản bị thay thế'
        break
      default:
        tag.color = ''
        tag.name = ''
        break
    }
    return tag
  },
  renderInternalDocumentTag: status => {
    const tag = {
      color: '',
      text: '',
    }
    switch (status) {
      case '100':
        tag.color = 'black'
        tag.name = 'Chưa duyệt'
        break
      case '101':
        tag.color = 'green'
        tag.name = 'Đã duyệt'
        break
      case '102':
        tag.color = 'blue'
        tag.name = 'Tạo danh sách công việc'
        break
      default:
        tag.color = ''
        tag.name = ''
        break
    }
    return tag
  },

  checkForDuplicates: array => {
    let valuesAlreadySeen = []

    for (let i = 0; i < array.length; i++) {
      let value = array[i]
      if (valuesAlreadySeen.indexOf(value) !== -1) {
        return true
      }
      valuesAlreadySeen.push(value)
    }
    return false
  },
  urlify(text) {
    const urlRegex = /(https?:\/\/[^\s]+)/g
    return text.replace(urlRegex, function (url) {
      return '<a href="' + url + '" target="_blank">' + url + '</a>'
    })
    // or alternatively
    // return text.replace(urlRegex, '<a href="$1">$1</a>')
  },
  exportExcel: (rows, filename = 'download') => {
    const fileType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
    const fileExtension = '.xlsx'
    const fileName = filename
    const ws = XLSX.utils.json_to_sheet(rows, { skipHeader: true })
    const wb = { Sheets: { data: ws }, SheetNames: ['data'] }
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    const data = new Blob([excelBuffer], { type: fileType })
    FileSaver.saveAs(data, fileName + fileExtension)
  },
  isIOSDevice: () => {
    const isPlatform = [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod',
    ].includes(navigator.platform)

    const isAgent = navigator.userAgent.includes('Mac')

    return (
      // iPad on iOS 13 detection
      isPlatform || isAgent
    )
  },

  getParameterByName: (name, url = window.location.href) => {
    name = name.replace(/[\[\]]/g, '\\$&')
    let regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
      results = regex.exec(url)
    if (!results) return null
    if (!results[2]) return ''
    return decodeURIComponent(results[2].replace(/\+/g, ' '))
  },
  formatCurrency: number => {
    if (!number) return
    return new Intl.NumberFormat().format(parseInt(number))
  },
  confirmDelete: handleDelete => {
    return confirm({
      title: 'Bạn có muốn xóa thông tin này không?',
      icon: <QuestionCircleOutlined style={{ color: 'red' }} />,
      okText: 'Đồng ý',
      cancelText: 'Không',
      okType: 'danger',
      onOk() {
        handleDelete()
      },
    })
  },
  async exportPDF(element, title, font) {
    const pdfContent = document.getElementById(element)
    const htmlContent = pdfContent.innerHTML
    //https://www.npmjs.com/package/html-to-pdfmake
    const html = buildSafePdfContent(htmlContent)
    const originURL = window.location.origin
    pdfMake.fonts = {
      TimesNewRoman: {
        normal: `${originURL}/assets/fonts/timesNewRoman/Times-New-Roman-Regular.ttf`,
        bold: `${originURL}/assets/fonts/timesNewRoman/Times-New-Roman-Bold.ttf`,
        italics: `${originURL}/assets/fonts/timesNewRoman/Times-New-Roman-Italic.ttf`,
        bolditalics: `${originURL}/assets/fonts/timesNewRoman/Times-New-Roman-BoldItalic.ttf`,
      },
      Roboto: {
        normal: `${originURL}/assets/fonts/roboto/Roboto-Regular.ttf`,
        bold: `${originURL}/assets/fonts/roboto/Roboto-Bold.ttf`,
        italics: `${originURL}/assets/fonts/roboto/Roboto-Italic.ttf`,
        bolditalics: `${originURL}/assets/fonts/roboto/Roboto-BoldItalic.ttf`,
      },
      CourierNew: {
        normal: `${originURL}/assets/fonts/courierNew/Courier-New-Regular.ttf`,
        bold: `${originURL}/assets/fonts/courierNew/Courier-New-Bold.ttf`,
        italics: `${originURL}/assets/fonts/courierNew/Courier-New-Italic.ttf`,
        bolditalics: `${originURL}/assets/fonts/courierNew/Courier-New-BoldItalic.ttf`,
      },
      Arial: {
        normal: `${originURL}/assets/fonts/arial/Arial-Regular.ttf`,
        bold: `${originURL}/assets/fonts/arial/Arial-Bold.ttf`,
        italics: `${originURL}/assets/fonts/arial/Arial-Italic.ttf`,
        bolditalics: `${originURL}/assets/fonts/arial/Arial-BoldItalic.ttf`,
      },
    }
    const documentDefinition = {
      content: html.content,
      defaultStyle: {
        font: font || 'Roboto',
      },
      images: html.images,
    }
    pdfMake.vfs = pdfFonts?.pdfMake?.vfs
    pdfMake.createPdf(documentDefinition).download(`${title}.pdf`)
  },
  async savePdfToFile(elementId, title, font = 'Roboto') {
    try {
      // Lấy phần tử DOM bằng ID
      const pdfContent = document.getElementById(elementId)
      if (!pdfContent) throw new Error('Không tìm thấy phần tử')

      // Chuẩn bị HTML để sanitize trước khi chuyển sang pdfMake
      const htmlContent = pdfContent.innerHTML

      // buildSafePdfContent sẽ lọc style không hợp lệ và giá trị NaN
      // Chuyển đổi HTML sang định dạng PDFMake
      const html = buildSafePdfContent(htmlContent)
      const originURL = window.location.origin

      // Định nghĩa font tùy chỉnh cho PDF
      pdfMake.fonts = {
        TimesNewRoman: {
          normal: `${originURL}/assets/fonts/timesNewRoman/Times-New-Roman-Regular.ttf`,
          bold: `${originURL}/assets/fonts/timesNewRoman/Times-New-Roman-Bold.ttf`,
          italics: `${originURL}/assets/fonts/timesNewRoman/Times-New-Roman-Italic.ttf`,
          bolditalics: `${originURL}/assets/fonts/timesNewRoman/Times-New-Roman-BoldItalic.ttf`,
        },
        Roboto: {
          normal: `${originURL}/assets/fonts/roboto/Roboto-Regular.ttf`,
          bold: `${originURL}/assets/fonts/roboto/Roboto-Bold.ttf`,
          italics: `${originURL}/assets/fonts/roboto/Roboto-Italic.ttf`,
          bolditalics: `${originURL}/assets/fonts/roboto/Roboto-BoldItalic.ttf`,
        },
      }

      // Tạo định nghĩa tài liệu cho PDFMake
      const documentDefinition = {
        content: html.content,
        defaultStyle: {
          font: font, // Font mặc định (có thể thay đổi)
        },
        images: html.images, // Các hình ảnh được tham chiếu
      }

      // Cấu hình hệ thống file ảo (virtual file system) để hỗ trợ font tùy chỉnh
      pdfMake.vfs = pdfFonts?.pdfMake?.vfs

      // Tạo PDF và chuyển đổi sang base64
      const base64Pdf = await new Promise((resolve, reject) => {
        try {
          pdfMake.createPdf(documentDefinition).getBase64(resolve)
        } catch (pdfError) {
          reject(pdfError)
        }
      })

      // Hàm chuyển base64 thành đối tượng File
      function base64ToFile(base64, title) {
        // Giải mã base64 thành chuỗi nhị phân
        const binaryString = atob(base64)
        const len = binaryString.length
        const bytes = new Uint8Array(len)

        // Chuyển đổi chuỗi nhị phân thành mảng byte
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i)
        }

        // Tạo Blob từ mảng byte
        const blob = new Blob([bytes], { type: 'application/pdf' })

        // Tạo đối tượng File từ Blob
        return new File([blob], `${title}.pdf`, { type: 'application/pdf' })
      }

      // Hàm tạo FileList từ đối tượng File
      function createFileList(file) {
        const dataTransfer = new DataTransfer()
        dataTransfer.items.add(file)
        return dataTransfer.files
      }

      // Chuyển đổi base64 thành đối tượng File
      const file = base64ToFile(base64Pdf, title)

      // Tạo và trả về FileList
      return createFileList(file)
    } catch (error) {
      console.error('Lỗi khi tạo PDF:', error)
      throw error // Ném lỗi nếu có vấn đề xảy ra
    }
  },
  checkRichType(type, delegateValue, unitValue, agencyValue) {
    return type === RISK_TYPE.DELEGATE
      ? delegateValue
      : type === RISK_TYPE.UNIT
      ? unitValue
      : agencyValue
  },
  getInfoUser(username, departmentWithUserList) {
    if (!departmentWithUserList) return
    const user = {
      full_name: null,
      image_id: null,
      username: username,
    }
    departmentWithUserList.forEach(department => {
      const userTarget = department?.users.find(
        user => user.user_name === username || user.user_code === username
      )
      if (userTarget) {
        user.full_name = userTarget.name_uppercase
          ? userTarget.name_uppercase
          : username
        user.image_id = userTarget.image_id
      }
    })
    return user
  },
  generateTypeString(startIndex, endIndex) {
    let result = ''

    for (let i = startIndex; i <= endIndex; i++) {
      if (i > startIndex) {
        result += '&'
      }
      result += `type=OTHER_${i}`
    }

    return result
  },
  compareVietnameseName(a, b) {
    const collator = new Intl.Collator('vi', {
      sensitivity: 'base', // bỏ qua hoa/thường & khác biệt dấu nhẹ
      numeric: false,
      ignorePunctuation: true,
    })

    return collator.compare(a, b)
  },
}

export default utils
